<template>
  <div class="page-wrap">
    <!-- 페이지 헤더 -->
    <PageHeader title="학교 관리" />

    <!-- Pill segmented filter + 검색 바 (한 줄 정렬) -->
    <div class="section-block toolbar-row">
      <div
        class="pill-filter"
        role="tablist"
        aria-label="학교 상태 필터"
      >
        <button
          v-for="item in pillItems"
          :key="item.value"
          type="button"
          role="tab"
          :aria-selected="tab === item.value"
          :class="['pill-filter__item', { 'is-active': tab === item.value }]"
          @click="onPillClick(item)"
        >
          <span class="pill-filter__label">{{ item.label }}</span>
          <span class="pill-filter__count">{{ item.count }}</span>
        </button>
      </div>

      <div class="school-search-bar__field">
        <v-autocomplete
          @keyup.enter="search(searchtext)"
          auto-select-first
          append-inner-icon="mdi-magnify"
          item-props
          density="compact"
          menu-icon=""
          variant="outlined"
          v-model="searchtext"
          :items="searchlist"
          color="primary"
          placeholder="학교명 또는 학교코드 검색"
          spellcheck="false"
          hide-details
        ></v-autocomplete>
      </div>
    </div>

    <!-- 테이블 영역 -->
    <!--
      [수정] 기존에는 <v-window> 내부에 동일 <allschool> 2개 인스턴스를 v-show로 토글하는 구조였음.
      두 인스턴스가 동시에 마운트되어 각자의 v-dialog(학교 등록/연락처/삭제 4개씩) teleport가
      같은 DOM에 중복 마운트 되면서 모달 오픈 시점에 "Cannot read properties of null (reading 'parentNode')"
      에러 발생. 동일 props를 전달하므로 단일 인스턴스로 통합한다.
    -->
    <div class="section-block table-block">
      <allschool :tab="tab" :schools="schoolsearchlist"></allschool>
    </div>
  </div>
</template>

<script>
import allschool from "./allschool.vue";
import PageHeader from "@/components/ui/PageHeader.vue";
import { getLastFromPath } from "@/router";

export default {
  data() {
    return {
      tab: null,
      searchlist: [],
      searchtext: null,
      re_searchlist: [],
      searchresult: "",
      statuslist: [],
      schoollist: [],
      schoolsearchlist: [],
    };
  },
  computed: {
    accountCounts() {
      const s = this.statuslist || {};
      const base = {
        안내필요: typeof s.needGuide === "number" ? s.needGuide : 0,
        정보대기: typeof s.waitInfo === "number" ? s.waitInfo : 0,
        발급필요: typeof s.needIssue === "number" ? s.needIssue : 0,
        발급완료: typeof s.issueComplete === "number" ? s.issueComplete : 0,
      };
      if (Object.values(base).every((v) => v === 0) && Array.isArray(this.schoollist) && this.schoollist.length) {
        const counts = { 안내필요: 0, 정보대기: 0, 발급필요: 0, 발급완료: 0 };
        for (const sc of this.schoollist) {
          if (counts[sc.accountStatus] !== undefined) counts[sc.accountStatus] += 1;
        }
        return counts;
      }
      return base;
    },
    pillItems() {
      const ac = this.accountCounts;
      const totalCount = Array.isArray(this.schoollist) ? this.schoollist.length : 0;
      const searchCount = Array.isArray(this.schoolsearchlist) ? this.schoolsearchlist.length : 0;
      return [
        { value: 1, label: "전체", count: totalCount, action: "goall" },
        { value: 9, label: "안내 필요", count: ac["안내필요"], action: "goneedguide" },
        { value: 6, label: "정보 대기", count: ac["정보대기"], action: "gowaitinginfo" },
        { value: 7, label: "발급 필요", count: ac["발급필요"], action: "needissue" },
        { value: 10, label: "발급 완료", count: ac["발급완료"], action: "issuecomplete" },
        { value: 8, label: "검색 결과", count: searchCount, action: null },
      ];
    },
  },
  async created() {
    const name = await this.$store.dispatch("GET_USER_NAME");
    sessionStorage.setItem("name", name);
    this.statuslist = await this.$store.dispatch("SCHOOL_STATUS");
    this.schoollist = await this.$store.dispatch("GET_SCHOOL_LIST");
    for (let i = 0; i < this.schoollist.length; i++) {
      this.schoollist[i].schoolName = `${this.schoollist[i].location} ${this.schoollist[i].schoolName}초등학교`;
    }
    this.schoolsearchlist = this.schoollist;
    const res = await this.$store.dispatch("GET_SEARCH_LIST");
    this.searchlist = Object.getOwnPropertyNames(res);
    // 상세 페이지에서 뒤로가기로 돌아온 경우에만 마지막 검색 상태를 복원한다.
    // 새로고침·새 로그인·메뉴 진입 등 그 외 경로는 검색을 초기화하고 전체 목록을
    // 보여준다 — 기존엔 무조건 복원해 이전 검색이 계속 남는 문제가 있었음 (qa-feedback-1020)
    const cameFromDetail = /^\/(user\/school-manage|guest\/guest-manage)\//.test(
      getLastFromPath() || ""
    );
    const savedSearch = sessionStorage.getItem("schoolManageSearch");
    if (cameFromDetail && savedSearch && savedSearch !== "null") {
      this.searchtext = savedSearch;
      this.search(savedSearch);
    } else {
      sessionStorage.removeItem("schoolManageSearch");
    }
  },
  components: {
    allschool,
    PageHeader,
  },
  watch: {
    searchtext(newtext) {
      this.re_searchlist = this.searchlist.filter((a) => a.includes(newtext));
    },
  },
  methods: {
    picksearch(text) {
      this.searchtext = text;
      this.search(text);
    },
    search(text) {
      if (!text || !text.trim()) {
        sessionStorage.removeItem("schoolManageSearch");
        this.schoolsearchlist = this.schoollist;
        this.tab = 8;
        return;
      }
      const trimmed = String(text).trim();
      sessionStorage.setItem("schoolManageSearch", trimmed);
      let schoollist = this.schoollist;
      const parenMatch = trimmed.match(/^(.+)\s*\(([^)]+)\)\s*$/);
      if (parenMatch) {
        // 자동완성 항목("지역 학교명 (코드)")으로 검색해도 코드로 좁히지 않고,
        // 지역 접두어를 뗀 학교 이름으로 동명 학교를 모두 노출한다.
        const namePart = parenMatch[1].trim();
        const tokens = namePart.split(" ");
        const baseName = tokens.length > 1 ? tokens.slice(1).join(" ") : namePart;
        schoollist = schoollist.filter((a) => (a.schoolName ?? "").includes(baseName));
      } else {
        schoollist = schoollist.filter(
          (a) =>
            (a.schoolName && a.schoolName.includes(trimmed)) ||
            (a.schoolCode && a.schoolCode.includes(trimmed))
        );
      }
      this.schoolsearchlist = schoollist;
      this.tab = 8;
    },
    gowaitinginfo() {
      this.schoolsearchlist = this.schoollist.filter((a) => a.accountStatus == "정보대기");
    },
    needissue() {
      this.schoolsearchlist = this.schoollist.filter((a) => a.accountStatus == "발급필요");
    },
    goneedguide() {
      this.schoolsearchlist = this.schoollist.filter((a) => a.accountStatus == "안내필요");
    },
    issuecomplete() {
      this.schoolsearchlist = this.schoollist.filter((a) => a.accountStatus == "발급완료");
    },
    goall() {
      this.schoolsearchlist = this.schoollist;
    },
    onPillClick(item) {
      this.tab = item.value;
      // 검색 결과 칩이 아닌 다른 칩을 고르면 저장해둔 검색 상태는 버린다
      // (뒤로가기 복원 시 칩 선택보다 검색이 우선돼버리는 것 방지).
      if (item.value !== 8) sessionStorage.removeItem("schoolManageSearch");
      if (item.action && typeof this[item.action] === "function") {
        this[item.action]();
      }
    },
    pad(num) {
      return num < 10 ? num.toString().padStart(2, "0") : num;
    },
  },
};
</script>

<style scoped>
/* 페이지 전체 스크롤 대신 목록(table-block) 안에서만 스크롤되도록 —
   DefaultLayout의 v-main(scrollable) + .app-content(height:100%) 위에서
   이 페이지만 height:100%로 고정하고 나머지는 내부 flex 자식에게 위임한다. */
.page-wrap {
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.table-block {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ── Pill Segmented Filter ── */
.pill-filter {
  display: flex;
  flex-wrap: nowrap;
  gap: var(--space-1, 4px);
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: var(--space-1, 4px);
}
.pill-filter::-webkit-scrollbar {
  display: none;
}
.pill-filter__item {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1, 4px);
  flex-shrink: 0;
  height: 32px;
  padding: 6px 14px;
  border-radius: var(--radius-pill, 999px);
  border: 1px solid var(--border-default, #d5d8de);
  background: var(--bg-surface, #fafaf8);
  color: var(--text-secondary, #525866);
  font-family: notosansregular;
  font-size: var(--font-small, 13px);
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  white-space: nowrap;
}
.pill-filter__item:hover {
  background: var(--bg-subtle, #e4e6eb);
  color: var(--text-primary, #22252c);
}
.pill-filter__item:focus-visible {
  outline: 2px solid var(--color-primary-600, #4482f0);
  outline-offset: 2px;
}
.pill-filter__item.is-active {
  background: var(--color-primary-600, #4482f0);
  border-color: var(--color-primary-600, #4482f0);
  color: var(--text-on-primary, #ffffff);
}
.pill-filter__item.is-active:hover {
  background: var(--color-primary-600, #4482f0);
  color: var(--text-on-primary, #ffffff);
}
.pill-filter__label {
  font-weight: 600;
}
.pill-filter__count {
  font-variant-numeric: tabular-nums;
  color: var(--text-tertiary, #838995);
}
.pill-filter__item.is-active .pill-filter__count {
  color: var(--text-on-primary, #ffffff);
  opacity: 0.85;
}

/* ── 필터 칩 + 검색 바: 한 줄 정렬 ── */
.toolbar-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.toolbar-row .pill-filter {
  flex: 1 1 0%;
  min-width: 0;
  padding-bottom: 0;
}
.school-search-bar__field {
  flex: 0 0 auto;
  width: 100%;
  max-width: 280px;
  min-width: 200px;
  font-family: notosansregular;
}
@media (max-width: 768px) {
  .toolbar-row {
    flex-wrap: wrap-reverse;
  }
  .school-search-bar__field {
    max-width: 100%;
  }
}

@media (max-width: 640px) {
  /* 모바일: 필터+검색 줄 sticky top */
  .toolbar-row {
    position: sticky;
    top: 0;
    z-index: 10;
    background: var(--bg-canvas);
    padding: var(--space-2) 0;
    margin-bottom: var(--space-2);
  }
  /* Pill: 모바일에서 살짝 축소 */
  .pill-filter__item {
    height: 28px;
    padding: 4px 10px;
    font-size: 12px;
  }
}
</style>
